import type { OrgAccount } from "./api.js";
import { formatBytes, trialCountdown } from "./billing.js";

/**
 * Where the organisation stands, as a sentence and three figures (LGN-170).
 *
 * The dashboard could say what an organisation was allowed to do and never
 * said where it stood: the plan, the trial and the two quotas lived on the
 * billing screen, which is `owner` and `billing` only, so an admin or a member
 * had no way to see that the trial ended on Friday or that the next site would
 * be refused. `get_account` (LGN-167) told a model all of it before any screen
 * told a person.
 *
 * **Presentation only.** Every number arrives from `GET /api/orgs/:orgId/account`,
 * which is `accountOrgForOwner` and `accountOrgSummary` in the domain and is
 * the same pair `GET /internal/account` calls for the MCP tool. Nothing here
 * adds, divides or compares a quota; it decides how a figure reads. That split
 * is the whole reason the two surfaces cannot disagree.
 *
 * `trialCountdown` comes from `billing.ts` rather than being written again
 * here, including its three-day threshold: the billing screen presses in the
 * last three days and this panel must press on the same day, not a day either
 * side of it.
 */

/**
 * The plan code a trial is on, mirroring `TRIAL_PLAN` in
 * `packages/domain/src/signup.ts`.
 *
 * Mirrored rather than imported: `@lagoon/domain` is a server package that
 * carries drizzle and the database, and no browser bundle may reach for it.
 * The value is a plan code in a column, so it changes only with a migration,
 * and being wrong about it costs one redundant clause in one sentence.
 */
const TRIAL_PLAN = "trial";

/**
 * The plan, as a person reads it: the column holds `pro`, a page says `Pro`.
 *
 * The same transformation `accountOrgSummary` applies, on purpose. There is a
 * display name for each plan in the billing catalog, but it arrives on
 * `GET /api/billing`, which two of the four roles may not read, and this panel
 * is for all four.
 */
export function planName(plan: string): string {
  return `${plan.charAt(0).toUpperCase()}${plan.slice(1)}`;
}

/** How a date reads on this screen. The browser's locale, as everywhere else here. */
function readableDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

export interface TrialLine {
  /** The whole sentence, ready to render. */
  readonly line: string;
  /**
   * Whether it belongs in a coloured box.
   *
   * True only in the last three days of a live trial. A trial is not a
   * problem: it is the state a new customer is meant to be in, and putting it
   * in a warning box for a fortnight teaches them to ignore warning boxes.
   * That is the billing screen's rule and this follows it.
   */
  readonly pressing: boolean;
}

/**
 * What to say about the trial, or nothing when there is no trial to discuss.
 *
 * Four states, and the fourth is the one worth naming: an expired trial on an
 * org whose plan is still `trial` has not moved anywhere, so "now on Trial"
 * would be a sentence that says nothing twice. `accountOrgSummary` drops the
 * plan in exactly that case, and this drops it too, so the screen and the
 * model phrase the same row the same way.
 *
 * `now` is a parameter for the same reason `trialCountdown` takes one: the
 * boundaries are the interesting part and a test must be able to stand on
 * them.
 */
export function trialLine(
  account: Pick<OrgAccount, "plan" | "trialEndsAt">,
  now: Date,
): TrialLine | null {
  const ends = account.trialEndsAt;
  if (ends === null) return null;
  const countdown = trialCountdown(ends, now);
  // An unparseable timestamp: `trialCountdown` answers null rather than NaN
  // days, and this says nothing rather than putting a broken date on a page.
  if (countdown === null) return null;

  const date = readableDate(ends);

  if (countdown.expired) {
    return {
      line:
        account.plan === TRIAL_PLAN
          ? `Trial ended ${date}.`
          : `Trial ended ${date}, now on ${planName(account.plan)}.`,
      pressing: false,
    };
  }

  const left =
    countdown.daysLeft === 0
      ? "That is today."
      : `${String(countdown.daysLeft)} ${countdown.daysLeft === 1 ? "day" : "days"} left.`;

  return countdown.ending
    ? { line: `Trial ends ${date}. ${left}`, pressing: true }
    : { line: `Trial ends ${date}.`, pressing: false };
}

export interface PlanMeter {
  readonly label: string;
  /** "3 of 5", already formatted, because a byte count and a seat count are not. */
  readonly value: string;
  /** The qualifier under the figure, or null when the figure stands alone. */
  readonly foot: string | null;
}

/** A count, grouped, so three figures of seats stay legible. */
function count(value: number): string {
  return value.toLocaleString();
}

/**
 * One "used of ceiling" figure, for a pair that may have no ceiling.
 *
 * `quota <= 0` is how an unlimited or unset entitlement arrives, exactly as
 * `readMeters` on the billing screen reads it. It shows the number with no
 * comparison rather than "3 of 0", which reads as a limit of nothing.
 */
function meter(
  label: string,
  used: number,
  ceiling: number,
  format: (value: number) => string,
): PlanMeter {
  if (ceiling <= 0) {
    return { label, value: format(used), foot: "No limit on this plan." };
  }
  return {
    label,
    value: `${format(used)} of ${format(ceiling)}`,
    foot: used >= ceiling ? "At the limit. The next one will be refused." : null,
  };
}

/**
 * The three meters, in the order the limits bite.
 *
 * Sites and storage are the two refusals an owner actually meets; seats is the
 * one an admin meets. Viewers are on the billing screen and deliberately not
 * here: nothing counts them yet, and a fourth tile reading "not measured"
 * would be three quarters of a panel about limits and one quarter about a
 * limit that does not exist.
 *
 * Seats is "used of limit" rather than "used of quota" because the domain
 * calls the ceiling a limit, and because an outstanding invitation spends a
 * seat at invite time: the used figure can be below the ceiling and the next
 * invitation still be refused. The member list is where that is explained;
 * this is where the number is.
 */
export function planMeters(account: Pick<OrgAccount, "sites" | "storage" | "seats">): PlanMeter[] {
  return [
    meter("Sites", account.sites.used, account.sites.quota, count),
    meter("Storage", account.storage.usedBytes, account.storage.quotaBytes, formatBytes),
    meter("Seats", account.seats.used, account.seats.limit, count),
  ];
}
